import type { LogOpts, TrackingEvent } from '../types';

/**
 * Conversions that represent a lead: the visitor has asked to be put in touch
 * with a professional, or taken the last step before doing so.
 *
 * Reporting a single `Lead` event across all of these gives an advertising
 * platform one signal to optimise against, instead of splitting the same intent
 * across several lower-volume events.
 */
const LEAD_EVENTS = new Set<TrackingEvent>([
    'CreatedEnquiry',
    'BookMeetingSubmit',
    'PhoneReveal',
    'BookConsultationReveal',
    'Website',
    'DownloadsClick',
    'DownloadsLoggedOutClick',
]);

/**
 * Conversions that are attributable to a professional but stop short of a lead.
 * They get a client-specific variant for reporting without inflating the lead
 * count that campaigns optimise against.
 */
const NON_LEAD_CONVERSIONS = new Set<TrackingEvent>([
    'EnquiryReveal',
    'BookMeetingReveal',
    'EnquiryEngagement',
    'SaveSubmit',
    'AddToCart',
    'InitiateCheckout',
    'Purchase',
]);

/**
 * Whether this event counts as a lead.
 */
export function isLeadEvent(event: TrackingEvent): boolean {
    return LEAD_EVENTS.has(event);
}

/**
 * Whether this event should also be reported as a client-specific conversion
 * when it belongs to the professional whose ad brought the visitor here.
 *
 * An explicit list rather than "every conversion", because each name here
 * becomes a signal an advertising platform can spend against, and that should
 * be a deliberate choice rather than a side effect of mapping a new event.
 *
 * Browsing events and HDE registration are absent by design: the first are not
 * conversions, and the second has no client professional.
 */
export function hasClientSpecificVariant(event: TrackingEvent): boolean {
    return LEAD_EVENTS.has(event) || NON_LEAD_CONVERSIONS.has(event);
}

/**
 * The professional a conversion belongs to, used to decide whether the
 * conversion should be credited to the ad the visitor arrived from.
 *
 * Prefers the dedicated `professionalId` option and falls back to the two
 * spellings that call sites already put in `data`. `LibraryItemID` is
 * deliberately not consulted: on product and project paths it holds the item's
 * id rather than the professional's, which would match the wrong client.
 */
export function resolveConversionProfessionalId(
    opts: Pick<LogOpts, 'professionalId' | 'data'>,
): number | undefined {
    const candidate =
        opts.professionalId ??
        opts.data?.['ProfessionalID'] ??
        opts.data?.['professionalID'];

    if (typeof candidate !== 'number' && typeof candidate !== 'string') {
        return undefined;
    }

    const professionalId = Number(candidate);

    return Number.isInteger(professionalId) && professionalId > 0
        ? professionalId
        : undefined;
}
